import { Pencil, Phone, Trash, WarningCircle, X } from "@phosphor-icons/react";
import React, { useEffect, useState } from "react";
import axiosInstance from "../../api/axiosInstance";
import dayjs from "dayjs";
import DragDrop from "../DragDrop";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isEqual from "lodash.isequal";
import PageLoader from "../loader/PageLoader";
import ImageLoader from "../loader/ImageLoader";
import { toast, Toaster } from "react-hot-toast";
import { toHotelISO, formatHotel } from "../../api/hotelTime";
import { uploadImageToServer } from "../../api/uploadImage";

const ManageGuest = () => {
    const { id, roomId: initialRoomId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isNewTab = searchParams.get("newTab") === "true";
    const hotelId = localStorage.getItem("hotelId");
    const [loading, setLoading] = useState(false);
    const [isEditingExistingGuest, setIsEditingExistingGuest] = useState(false);

    // State for booking data and form
    const [initialBookingData, setInitialBookingData] = useState(null);
    const [bookingForm, setBookingForm] = useState({
        checkin_date: "",
        checkout_date: "",
        name: "",
        ota_id: "",
        countryCode: "91",
        contact: "",
    });
    // New states for change tracking
    const [isGuestChanged, setIsGuestChanged] = useState(false);
    const [isRoomNumberChanged, setIsRoomNumberChanged] = useState(false);
    const [previousGuestForm, setPreviousGuestForm] = useState(null);
    const [previousRoomNumber, setPreviousRoomNumber] = useState("");
    const [showChangeAlert, setShowChangeAlert] = useState(false);
    const [pendingRoomSelection, setPendingRoomSelection] = useState(null);
    const [originalContact, setOriginalContact] = useState(null);

    // Room States
    const [virtualRooms, setVirtualRooms] = useState([]);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [isCheckoutCompleted, setIsCheckoutCompleted] = useState(false);
    const [roomNumber, setRoomNumber] = useState("");
    // A room number is the check-in: reassigning it needs the open requests, the
    // PMS and the guest's welcome details moved with it, none of which this screen
    // does. Assign once here; moves go through a Room Change request.
    const [isRoomNumberLocked, setIsRoomNumberLocked] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [errors, setErrors] = useState({});

    // Guest Management States
    const [currentGuestData, setCurrentGuestData] = useState([]);
    const [guestForm, setGuestForm] = useState({
        name: "",
        countryCode: "91",
        contact: "",
        verification_id: null,
    });

    // State to track changes
    const [isDataChanged, setIsDataChanged] = useState(false);
    const [cardLoading, setCardLoading] = useState(false);

    // Extend dayjs
    dayjs.extend(utc);
    dayjs.extend(timezone);

    // Fetch booking details on component mount
    useEffect(() => {
        const fetchBookingDetailsAndRooms = async () => {
            try {
                setLoading(true);
                const response = await axiosInstance.get(
                    `/api/v1/hotels/${hotelId}/bookings/${id}`,
                );
                const bookingData = response.data;

                // Separate country code and contact number
                let countryCode = "91";
                let contact = bookingData.contact;
                if (bookingData.contact) {
                    const contactStr = bookingData.contact.toString();
                    if (contactStr.startsWith("91")) {
                        countryCode = "91";
                        contact = contactStr.slice(2);
                    } else {
                        // Add more country code detection logic if needed
                        contact = contactStr;
                    }
                }

                // Set initial and form data
                setInitialBookingData({
                    ...bookingData,
                    countryCode,
                    contact,
                });

                setBookingForm({
                    checkin_date: formatHotel(bookingData.checkin_date, "YYYY-MM-DD"),
                    checkout_date: formatHotel(bookingData.checkout_date, "YYYY-MM-DD"),
                    name: bookingData.name,
                    ota_id: bookingData.ota_id || "",
                    countryCode,
                    contact,
                });

                const roomsResponse = await axiosInstance.get(
                    `/api/v1/hotels/${hotelId}/bookings/${id}/booked-rooms`,
                );
                const rooms = roomsResponse.data.data;
                setVirtualRooms(rooms);
                setLoading(false);
                // Handle initial room selection if roomId is provided
                if (initialRoomId) {
                    const initialRoom = rooms.find(
                        (room) => room.id === initialRoomId,
                    );
                    if (initialRoom) {
                        await handleRoomSelection(initialRoom);
                    }
                }
            } catch (error) {
                console.error(
                    "Error fetching booking details or rooms:",
                    error,
                );
                setLoading(false);
            }
        };

        fetchBookingDetailsAndRooms();
    }, [id]);

    const handleRoomSelection = async (room) => {
        // Check if there are unsaved changes
        if (isGuestChanged || isRoomNumberChanged) {
            setShowChangeAlert(true);
            setPendingRoomSelection(room);
            return;
        }

        // If no changes, proceed with room selection as before
        await performRoomSelection(room);
    };

    const performRoomSelection = async (room) => {
        // Reset change tracking states
        setCardLoading(true);
        setIsGuestChanged(false);
        setIsRoomNumberChanged(false);
        setPreviousGuestForm(null);
        setPreviousRoomNumber("");

        // Reset guest data and checkout status
        setCurrentGuestData(null);
        setRoomNumber("");
        setIsCheckoutCompleted(false);
        setGuestForm({
            name: "",
            countryCode: "91",
            contact: "",
            verification_id: "",
        });

        // Set selected room
        setSelectedRoom(room);

        try {
            setLoading(true);
            const hotelId = localStorage.getItem("hotelId");
            // Fetch room-specific guest data
            const response = await axiosInstance.get(
                `/api/v1/hotels/${hotelId}/bookings/${room.booking_id}/booked-rooms/${room.id}`,
            );

            setIsCheckoutCompleted(
                response.data.is_checkout_completed || false,
            );

            setRoomNumber(response.data.room_number);
            setIsRoomNumberLocked(
                response.data.room_number !== null &&
                    response.data.room_number !== undefined &&
                    response.data.room_number !== "",
            );

            const guests = response.data.guests || [];

            // Assign unique IDs to each guest
            const guestsWithIds = guests.map((guest, index) => ({
                ...guest,
                id: index + 1, // Assign incremental IDs starting from 1
            }));

            if (response.data.guests && response.data.guests.length > 0) {
                const guestData = guestsWithIds
                    ? await Promise.all(
                          guestsWithIds.map(async (guest) => {
                              const imageResponse =
                                  await getImageByVerificationId(
                                      guest.verification_id,
                                  );

                              return {
                                  ...guest,
                                  image: imageResponse,
                              };
                          }),
                      )
                    : [];

                // Set current guest data if guests exist
                setCurrentGuestData(guestData);
                // console.log("000000000000", guestData);
                setIsEditing(false); // Ensure guest fields are hidden
            } else {
                // Show phone verify section if no guests exist
                setCurrentGuestData([]);
                setIsEditing(false); // Ensure guest fields are hidden
            }
            // console.log("guest data------------", currentGuestData);
        } catch (error) {
            console.error("Error fetching room-specific data:", error);
            toast.error("Error fetching guest data for this room.");
        } finally {
            setLoading(false);
            setCardLoading(false);
        }
    };

    const handleDiscardChanges = () => {
        // Revert guest form to previous state
        if (previousGuestForm) {
            setGuestForm(previousGuestForm);
        }

        // Revert room number
        if (previousRoomNumber !== "") {
            setRoomNumber(previousRoomNumber);
        }

        // Reset change flags
        setIsGuestChanged(false);
        setIsRoomNumberChanged(false);
        setShowChangeAlert(false);

        // Perform room selection if there was a pending selection
        if (pendingRoomSelection) {
            performRoomSelection(pendingRoomSelection);
            setPendingRoomSelection(null);
        }
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const newFormData = { ...bookingForm, [name]: value };

        setBookingForm(newFormData);

        // Check if data has changed from initial state
        if (initialBookingData) {
            const isChanged = !isEqual(
                {
                    checkin_date: formatHotel(initialBookingData.checkin_date, "YYYY-MM-DD"),
                    checkout_date: formatHotel(initialBookingData.checkout_date, "YYYY-MM-DD"),
                    name: initialBookingData.name,
                    ota_id: initialBookingData.ota_id || "",
                    countryCode: initialBookingData.countryCode || "91",
                    contact: initialBookingData.contact,
                },
                newFormData,
            );
            setIsDataChanged(isChanged);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!guestForm.name) newErrors.name = "Name is required!";
        if (!guestForm.contact) newErrors.contact = "Contact is required";
        if (!guestForm.verification_id)
            newErrors.verification_id = "Verification Id is required!";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleGuestInputChange = (e) => {
        setErrors("");
        const { name, value } = e.target;
        if (name === "contact" && !/^\d*$/.test(value)) {
            // Allow only numbers
            return;
        }

        // Store previous guest form data if this is the first change
        if (!isGuestChanged) {
            setPreviousGuestForm(guestForm);
        }

        setGuestForm((prev) => ({
            ...prev,
            [name]: value,
        }));
        if (name === "contact") {
            setErrors((prev) => ({ ...prev, verifyContact: "" }));
        }
        setIsGuestChanged(true);
    };

    const handleRoomNumberChange = (e) => {
        const newRoomNumber = e.target.value;

        // Store previous room number if this is the first change
        if (!isRoomNumberChanged) {
            setPreviousRoomNumber(roomNumber);
        }

        setRoomNumber(newRoomNumber);
        setIsRoomNumberChanged(true);
    };

    // Image related

    const handleFileUpload = async (files) => {
        if (files.length === 0) return;

        try {
            const file = files[0];
            const uploadUrlResponse = await axiosInstance.get(
                `/api/v1/images/upload-presigned-url?file_name=${file.name}`,
            );
            const { key, url } = uploadUrlResponse.data;
            const isSuccess = await uploadImageToServer(file, url);

            // console.log("key-----------", key);

            if (isSuccess) {
                setGuestForm((prev) => ({
                    ...prev,
                    verification_id: key,
                }));
                // console.log("File uploaded successfully. Key:", key);
                isGuestChanged(true);
                return;
            }
        } catch (error) {
            console.error("Error uploading image:", error);
            return false;
        }
    };

    const getImageByVerificationId = async (key) => {
        try {
            const response = await axiosInstance.get(
                `/api/v1/images/download-presigned-url?key=${key}`,
            );
            return response.data.url;
        } catch (error) {
            console.error("Error fetching image URL:", error);
            return { key, url: "", loading: false, error: true };
        }
    };

    const handleVerifyUser = async () => {
        const fullContact = parseInt(
            guestForm.countryCode + guestForm.contact,
            10,
        );

        if (!guestForm.contact || guestForm.contact.length < 10) {
            setErrors((prev) => ({
                ...prev,
                verifyContact: "Please enter a 10-digit phone number.",
            }));
            return;
        }

        try {
            const response = await axiosInstance.get(
                `/api/v1/users?contact=${fullContact}`,
            );
            const userData = response.data.data[0];

            if (userData) {
                setGuestForm({
                    name: userData.name,
                    countryCode: guestForm.countryCode,
                    contact: guestForm.contact,
                    verification_id: userData.verification_id,
                });
                setIsEditing(true);
                setIsEditingExistingGuest(false);
                setErrors((prev) => ({ ...prev, verifyContact: "" }));
            } else {
                setGuestForm({
                    name: "",
                    countryCode: guestForm.countryCode,
                    contact: guestForm.contact,
                    verification_id: "",
                });
                setIsEditing(true);
                setIsEditingExistingGuest(false);
                setErrors((prev) => ({ ...prev, verifyContact: "" }));
            }
        } catch (error) {
            console.error("Error verifying user:", error);
            setErrors((prev) => ({
                ...prev,
                verifyContact: "Failed to verify user. Please try again.",
            }));
        }
    };

    // Render rooms section
    const renderVirtualRooms = () => {
        return (
            <div className="virRooms flex gap-2 max-w-full min-h-14 overflow-x-auto">
                <style jsx="true">{`
                    .virRooms::-webkit-scrollbar {
                        width: 6px;
                        height: 8px;
                    }
                    .virRooms::-webkit-scrollbar-track {
                        background: #d8d8d8;
                        width: 10px;
                    }
                    .virRooms::-webkit-scrollbar-thumb {
                        background-color: #888;
                        border-radius: 10px;
                    }
                    .virRooms::-webkit-scrollbar-thumb:hover {
                        background-color: #555;
                    }
                `}</style>
                {virtualRooms &&
                    [...virtualRooms]
                        .sort((a, b) => a.id.localeCompare(b.id))
                        .map((room, index) => (
                            <button
                                key={room.id}
                                onClick={() => handleRoomSelection(room)}
                                className={`px-4 py-1 rounded-lg flex-shrink-0 flex items-center gap-2 my-1 ${
                                    selectedRoom?.id === room.id
                                        ? "bg-[#ff7378] text-white"
                                        : "bg-gray-100"
                                }`}
                            >
                                <span>Room {index + 1}</span>
                                <span className="bg-[#FAE2E6] px-3 py-0.5 rounded-full text-[#831940]">
                                    {room.requests && room.requests.length > 0
                                        ? room.requests.length
                                        : "0"}
                                </span>
                            </button>
                        ))}
            </div>
        );
    };

    const handleEditGuest = (guest) => {
        if (isCheckoutCompleted) {
            return;
        }

        setIsEditing(true);
        setIsEditingExistingGuest(true);

        // Separate country code and contact number
        let countryCode = "91";
        let contact = guest.contact;

        // Check if contact starts with a country code
        const contactStr = guest.contact.toString();
        if (contactStr.startsWith("91")) {
            countryCode = "91";
            contact = contactStr.slice(2);
        } else if (contactStr.startsWith("1")) {
            countryCode = "1";
            contact = contactStr.slice(1);
        }

        setGuestForm({
            id: guest.id, // Include the ID for subsequent updates
            name: guest.name || "",
            countryCode: countryCode,
            contact: contact,
            verification_id: guest.verification_id || "", // Include the verification_id
        });

        setOriginalContact(guest.contact); // Track the original contact for reference
    };

    const handleDeleteGuest = (guestId) => {
        if (isCheckoutCompleted) {
            return;
        }

        setCurrentGuestData((prev) => {
            const updatedGuests = prev.filter((guest) => guest.id !== guestId);
            return updatedGuests;
        });

        setIsGuestChanged(true); // Indicate that the guest data has changed
    };

    // Render guest management section
    const renderGuestManagement = () => {
        // If no room is selected, show nothing
        if (!selectedRoom) return null;

        // If no guest data, show verify user screen
        if (!currentGuestData) {
            return <div></div>;
        }
        // If guest data exists, show guest details
        return (
            <div>
                {/* Guest details rendering logic */}

                <div className="overflow-auto h-56">
                    {currentGuestData &&
                        currentGuestData.map((guest, index) => (
                            <div
                                className="border-[1.5px] rounded-lg overflow-hidden h-full bg-[#EEF0FB]"
                                key={index}
                            >
                                {cardLoading ? (
                                    <ImageLoader />
                                ) : (
                                    <>
                                        <img
                                            src={guest?.image || ""}
                                            alt="verification_id"
                                            className="h-36 w-full bg-center"
                                        />
                                      
                                        <div className="px-1.5 text-xs">
                                            <p className="text-[#262B33] font-bold text-lg">
                                                {guest?.name}
                                            </p>
                                            <p>{`${
                                                guest?.contact || "N/A"
                                            }`}</p>
                                            <div className="flex items-center justify-end">
                                                {!isCheckoutCompleted && (
                                                    <div className="flex items-center gap-1">
                                                        <button
                                                            className="border-[#F5CCD3] text-[#D02746] p-1 rounded-md border-[1.5px] bg-white"
                                                            onClick={() =>
                                                                handleDeleteGuest(
                                                                    guest.id,
                                                                )
                                                            }
                                                        >
                                                            <Trash size={16} />
                                                        </button>
                                                        <button
                                                            className="border-gray-400 p-1 rounded-md border-[1.5px] bg-white"
                                                            // Add edit guest logic
                                                            onClick={() =>
                                                                handleEditGuest(
                                                                    guest,
                                                                )
                                                            }
                                                        >
                                                            <Pencil size={16} />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                </div>
            </div>
        );
    };

    const renderChangeAlert = () => {
        if (!showChangeAlert) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1002]">
                <div className="bg-white p-6 rounded-lg">
                    <div className="flex items-center gap-2 mb-4">
                        <WarningCircle size={24} className="text-yellow-500" />
                        <h3 className="text-lg font-semibold">
                            Unsaved Changes
                        </h3>
                    </div>
                    <p className="mb-4">
                        You have unsaved changes. Do you want to discard these
                        changes?
                    </p>
                    <div className="flex justify-end gap-2">
                        <button
                            onClick={() => setShowChangeAlert(false)}
                            className="px-4 py-2 border rounded"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleDiscardChanges}
                            className="px-4 py-2 bg-red-500 text-white rounded"
                        >
                            Discard
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Handle update booking
    const handleUpdateBooking = async () => {
        try {
            const updatesRequired = [];
            const guestsWithoutIds = currentGuestData.map(
                ({ id, ...guest }) => guest,
            ); // Remove 'id' field
            // console.log("(((((((((", guestsWithoutIds);
            if (isDataChanged) {
                const updatedBookingData = {
                    ...initialBookingData,
                    checkin_date: toHotelISO(bookingForm.checkin_date),
                    checkout_date: toHotelISO(bookingForm.checkout_date),
                    name: bookingForm.name,
                    ota_id: bookingForm.ota_id,
                    contact: parseInt(
                        bookingForm.countryCode + bookingForm.contact,
                    ),
                    guest_email: initialBookingData.booking_email,
                };

                updatesRequired.push(
                    axiosInstance.put(
                        `/api/v1/hotels/${hotelId}/bookings/${id}`,
                        updatedBookingData,
                    ),
                );
            }

            if (isRoomNumberChanged || isGuestChanged) {
                const updateData = {
                    room_number: parseInt(roomNumber),
                    guest_details: guestsWithoutIds
                        ? guestsWithoutIds.map((guest) => ({
                              name: guest.name,
                              contact: parseInt(guest.contact),
                              verification_id: guest.verification_id,
                          }))
                        : [],
                    number_of_guests: guestsWithoutIds.length,
                };

                // console.log(
                //     "this is the data i am submitting to room",
                //     updateData,
                // );

                if (selectedRoom) {
                    updatesRequired.push(
                        axiosInstance.put(
                            `/api/v1/hotels/${hotelId}/bookings/${selectedRoom.booking_id}/booked-rooms/${selectedRoom.id}`,
                            updateData,
                        ),
                    );
                }
            }

            // Execute all required updates
            if (updatesRequired.length > 0) {
                await Promise.all(updatesRequired);

                // Reset change flags and initial data
                setIsDataChanged(false);
                setIsGuestChanged(false);
                setIsRoomNumberChanged(false);

                toast.success("Update completed successfully");
            }
        } catch (error) {
            console.error("Error updating booking:", error);
        }
    };

    function handleClose() {
        // Get the current path
        const currentPath = window.location.pathname;

        // Find the index of "manage-room" or "manage-guests"
        const splitPath = currentPath.split("/");
        const manageIndex = splitPath.findIndex(
            (segment) =>
                segment === "manage-room" || segment === "manage-guest",
        );

        if (manageIndex !== -1) {
            // Reconstruct the path up to "manage-room" or "manage-guests"
            const updatedPath = splitPath.slice(0, manageIndex).join("/");
            // Navigate to the updated path
            navigate(updatedPath);
        } else {
            console.warn(
                "No 'manage-room' or 'manage-guests' found in the URL",
            );
        }
    }

    const handleUpdateGuest = async () => {
        const updatedGuest = {
            id: guestForm.id,
            name: guestForm.name,
            contact: parseInt(guestForm.countryCode + guestForm.contact, 10),
            verification_id: guestForm.verification_id,
        };

        let newImage = null;
        const existingGuest = currentGuestData.find(
            (guest) => guest.id === updatedGuest.id,
        );

        if (existingGuest?.verification_id !== updatedGuest.verification_id) {
            // Fetch new image if verification_id has changed
            newImage = await getImageByVerificationId(
                updatedGuest.verification_id,
            );
        }

        setCurrentGuestData((prev) =>
            prev.map((guest) =>
                guest.id === updatedGuest.id
                    ? {
                          ...guest,
                          ...updatedGuest,
                          image: newImage || guest.image, // Use new image if available, else retain old one
                      }
                    : guest,
            ),
        );

        // Reset the form
        setGuestForm({
            id: null,
            name: "",
            countryCode: "91",
            contact: "",
            verification_id: "",
        });

        setOriginalContact(null);
        setIsEditing(false);
    };

    const handleAddGuest = async () => {
        if (!validateForm()) return;

        setCardLoading(true);

        try {
            const imageUrl = await getImageByVerificationId(
                guestForm.verification_id,
            );

            // console.log("image url", imageUrl);

            const newGuest = {
                id: currentGuestData ? currentGuestData.length + 1 : 1, // Assign next available ID
                name: guestForm.name,
                contact: parseInt(
                    guestForm.countryCode + guestForm.contact,
                    10,
                ),
                verification_id: guestForm.verification_id, // Save the key
                image: imageUrl,
            };

            // Ensure currentGuestData is an array before adding a new guest
            setCurrentGuestData((prev) => {
                if (!prev) return [newGuest]; // If prev is null or undefined, start with an array containing the new guest
                return [...prev, newGuest]; // Otherwise, append the new guest to the existing array
            });

            // Reset the guest form for adding more guests
            setGuestForm({
                name: "",
                countryCode: "91",
                contact: "",
                verification_id: "",
            });
            setIsEditing(false); // Hide guest fields after adding
        } catch (error) {
            console.log("error occurred", error);
        } finally {
            setCardLoading(false);
        }
    };

    const activateUpdateBtn =
        isRoomNumberChanged || isDataChanged || isGuestChanged;

    return (
        <div className="fixed top-0 left-0 h-screen w-screen bg-black bg-opacity-60 flex items-center justify-center z-[1001] overflow-auto">
            {loading && <PageLoader />}
            <form
                className="popup-content bg-white p-6 rounded-lg text-sm flex flex-col gap-2 w-[80%] md:w-[70%] lg:w-[50%] top-10 max-h-[98vh] overflow-y-auto"
                onSubmit={(e) => e.preventDefault()}
            >
                <style jsx="true">{`
                    form::-webkit-scrollbar {
                        width: 6px;
                        height: 8px;
                    }
                    form::-webkit-scrollbar-track {
                        background: #d8d8d8;
                        width: 10px;
                    }
                    form::-webkit-scrollbar-thumb {
                        background-color: #888;
                        border-radius: 10px;
                    }
                    form::-webkit-scrollbar-thumb:hover {
                        background-color: #555;
                    }
                `}</style>
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold">Manage Guests</h2>
                    <button
                        onClick={handleClose}
                        className="hover:bg-[#F2F7FF] p-1.5 border-[1.5px] rounded-md"
                        type="button"
                    >
                        <X weight="bold" size={18} />
                    </button>
                </div>
                <div className="flex items-center justify-between">
                    <div className="w-1/3">
                        <p className="text-[#8C9199] mb-2">Booking ID</p>
                        <p className="truncate pr-3" title={id}>
                            {id}
                        </p>
                    </div>
                    <div className="w-1/3">
                        <p className="text-[#8C9199] mb-2">Check-in Date</p>
                        <input
                            type="date"
                            className="p-1 rounded-md border-[1.5px]"
                            name="checkin_date"
                            value={bookingForm.checkin_date}
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="w-1/3">
                        <p className="text-[#8C9199] mb-2">Check-out Date</p>
                        <input
                            type="date"
                            className="p-1 rounded-md border-[1.5px]"
                            name="checkout_date"
                            value={bookingForm.checkout_date}
                            onChange={handleInputChange}
                        />
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-1/4">
                        <p className="text-[#8C9199] mb-2">Booking Name</p>
                        <input
                            type="text"
                            className="p-1 rounded-md border-[1.5px] w-full"
                            name="name"
                            value={bookingForm.name}
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="w-1/4">
                        <p className="text-[#8C9199] mb-2">OTA ID</p>
                        <input
                            type="text"
                            className="p-1 rounded-md border-[1.5px] w-full"
                            name="ota_id"
                            value={bookingForm.ota_id}
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="w-1/2">
                        <p className="text-[#8C9199] mb-2">Contact</p>
                        <div className="border-[1.5px] rounded-md flex w-full overflow-hidden">
                            <select
                                name="countryCode"
                                className="p-1.5 border-r-[1.5px] outline-none"
                                value={bookingForm.countryCode}
                                onChange={handleInputChange}
                            >
                                <option value="91">+91</option>
                                <option value="1">+1</option>
                                <option value="44">+44</option>
                                <option value="86">+86</option>
                                <option value="81">+81</option>
                            </select>
                            <div className="rounded-md w-full flex items-center gap-2 px-1.5">
                                <Phone size={19} />
                                <input
                                    type="text"
                                    placeholder="Enter contact number"
                                    className="w-full py-1.5 outline-none"
                                    name="contact"
                                    maxLength={10}
                                    value={bookingForm.contact}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {/* Room details start */}
                {renderVirtualRooms()}

                <div className="flex border-t-[1.5px]">
                    <div className="w-[40%] p-2 border-r-[1.5px]">
                        {!isCheckoutCompleted &&
                            (isEditingExistingGuest ? (
                                <button
                                    onClick={handleUpdateGuest}
                                    className={`bg-[#ff7378] p-1.5 rounded-md outline-none border-none w-full mb-2 ${
                                        isGuestChanged
                                            ? "bg-[#ff7378] cursor-pointer"
                                            : "bg-[#f9999c] cursor-not-allowed"
                                    }`}
                                    disabled={!isGuestChanged} // Disable button if no changes are made
                                >
                                    Update Guest
                                </button>
                            ) : (
                                <button
                                    onClick={handleAddGuest}
                                    className={`bg-[#ff7378] p-1.5 rounded-md outline-none border-none w-full mb-2 ${
                                        guestForm.name &&
                                        guestForm.contact &&
                                        guestForm.verification_id
                                            ? "bg-[#ff7378] cursor-pointer"
                                            : "bg-[#f9999c] cursor-not-allowed"
                                    }`}
                                    disabled={
                                        !guestForm.name ||
                                        !guestForm.contact ||
                                        !guestForm.verification_id
                                    } // Disable button if fields are incomplete
                                >
                                    Add Guest
                                </button>
                            ))}

                        {renderGuestManagement()}
                    </div>
                    <div className="w-[60%] p-2 flex flex-col gap-2">
                        {/* Room Number */}
                        <div>
                            <p>Room Number</p>
                            <input
                                type="number"
                                className={`w-full p-1.5 outline-none border-[1.5px] rounded-md ${
                                    isRoomNumberLocked
                                        ? "bg-[#F5F6FA] text-[#98A0AE] cursor-not-allowed"
                                        : ""
                                }`}
                                name="room_number"
                                placeholder={`${
                                    roomNumber ? "Enter room number" : "NA"
                                }`}
                                onChange={handleRoomNumberChange}
                                value={roomNumber}
                                disabled={isRoomNumberLocked}
                            />                        </div>
                        {!isEditing || isCheckoutCompleted ? (
                            <div className="">
                                <p className="text-[#262B33] font-medium mb-1">
                                    Phone
                                </p>
                                <div className="border-[1.5px] rounded-md flex w-full overflow-hidden">
                                    <select
                                        name="countryCode"
                                        className="p-1.5 w-[50%] border-r-[1.5px] outline-none"
                                        value={guestForm.countryCode}
                                        onChange={handleGuestInputChange}
                                    >
                                        <option value="91">India (+91)</option>
                                        <option value="1">USA (+1)</option>
                                        {/* Add more country codes */}
                                    </select>
                                    <div className="rounded-md w-full flex items-center gap-2 px-1.5">
                                        <Phone size={19} />
                                        <input
                                            type="text"
                                            placeholder="Enter contact number"
                                            className="w-full py-1.5 outline-none"
                                            name="contact"
                                            maxLength={10}
                                            value={guestForm.contact}
                                            onChange={handleGuestInputChange}
                                        />
                                    </div>
                                </div>
                                <p className="text-[#ff7378] flex gap-1 items-center text-xs">
                                    {errors.verifyContact && (
                                        <>
                                            <WarningCircle size={14} />
                                            {errors.verifyContact}
                                        </>
                                    )}
                                </p>

                                <button
                                    className="bg-[#ff7378] p-1.5 rounded-md outline-none border-none w-full my-2"
                                    onClick={handleVerifyUser}
                                >
                                    Verify User
                                </button>
                            </div>
                        ) : (
                            <div>
                                {/* Guest input fields */}
                                <div>
                                    <p className="text-[#262B33] font-medium mb-1">
                                        Name
                                    </p>
                                    <input
                                        type="text"
                                        placeholder="Enter guest name"
                                        className="p-1.5 rounded-md border-[1.5px] w-full"
                                        name="name"
                                        value={guestForm.name}
                                        onChange={handleGuestInputChange}
                                    />
                                </div>

                                {/* Phone input */}
                                <div>
                                    <p className="text-[#262B33] font-medium mb-1">
                                        Phone
                                    </p>
                                    <div className="border-[1.5px] rounded-md flex w-full overflow-hidden">
                                        <select
                                            name="countryCode"
                                            className="p-1.5 w-[50%] border-r-[1.5px] outline-none"
                                            value={guestForm.countryCode}
                                            onChange={handleGuestInputChange}
                                        >
                                            <option value="91">
                                                India (+91)
                                            </option>
                                            <option value="1">USA (+1)</option>
                                            {/* Add more country codes */}
                                        </select>
                                        <div className="rounded-md w-full flex items-center gap-2 px-1.5">
                                            <Phone size={19} />
                                            <input
                                                type="text"
                                                placeholder="Enter contact number"
                                                className="w-full py-1.5 outline-none"
                                                name="contact"
                                                maxLength={10}
                                                value={guestForm.contact}
                                                onChange={
                                                    handleGuestInputChange
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Verification ID */}
                                <div>
                                    <p className="text-[#262B33] font-medium mb-1">
                                        Verification ID
                                    </p>
                                    {guestForm.verification_id ? (
                                        <div className="flex justify-between items-center p-2 border rounded text-green-300">
                                            <span>
                                                {guestForm.verification_id}
                                            </span>
                                            <button
                                                onClick={() =>
                                                    setGuestForm((prev) => ({
                                                        ...prev,
                                                        verification_id: "",
                                                    }))
                                                }
                                                className="text-red-500"
                                            >
                                                <Trash size={16} />
                                            </button>
                                        </div>
                                    ) : (
                                        <DragDrop
                                            onFilesUploaded={handleFileUpload}
                                            acceptedFileTypes={{
                                                "image/*": [
                                                    ".jpeg",
                                                    ".png",
                                                    ".jpg",
                                                ],
                                            }}
                                            maxFiles={1}
                                            dragDropText="Drag and drop ID image here"
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <button
                    className={`p-2 w-full rounded-md font-medium text-white
                        ${
                            activateUpdateBtn
                                ? "bg-[#ff7378] cursor-pointer"
                                : "bg-gray-400 cursor-not-allowed"
                        }`}
                    disabled={!activateUpdateBtn}
                    type="button"
                    onClick={handleUpdateBooking}
                >
                    Update
                </button>

                {renderChangeAlert()}
            </form>{" "}
            <Toaster
                position="bottom-right"
                reverseOrder={false}
                transition="Bounce"
                style={{ marginBottom: "25%" }}
            />
        </div>
    );
};

export default ManageGuest;
